export class EngineController extends EventTarget {
  #worker = null;
  #ready = null;
  #active = null;

  async initialize() {
    if (this.#ready) {
      return this.#ready;
    }
    this.#worker = new Worker(new URL("./engine-worker.js", import.meta.url), {
      type: "module",
    });
    this.#worker.onmessage = ({ data }) => this.#handle(data);
    this.#worker.onerror = (event) =>
      this.dispatchEvent(new CustomEvent("error", { detail: event.message }));
    this.#ready = new Promise((resolve, reject) => {
      const timer = setTimeout(
        () => reject(new Error("Engine readiness timed out")),
        55_000,
      );
      this.addEventListener(
        "ready",
        () => {
          clearTimeout(timer);
          resolve(undefined);
        },
        { once: true },
      );
      this.addEventListener(
        "error",
        (event) => {
          clearTimeout(timer);
          reject(new Error(event.detail));
        },
        { once: true },
      );
    });
    const base = new URL("./generated/stockfish/", document.baseURI);
    const workerUrl = new URL("stockfish-18-lite-single.js", base);
    workerUrl.searchParams.set("wrapper", "18.0.8-binary-host-1");
    this.#worker.postMessage({
      type: "INIT",
      workerUrl: workerUrl.href,
      timeoutMs: 50_000,
    });
    return this.#ready;
  }

  async search(fen, difficulty) {
    const result = await this.analyze(fen, difficulty);
    return result.move;
  }

  async analyze(fen, difficulty) {
    await this.initialize();
    this.stop();
    const searchId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => {
          if (this.#active?.searchId === searchId) {
            this.#active = null;
            this.#worker?.postMessage({ type: "STOP", searchId });
          }
          reject(new Error("Engine search timed out"));
        },
        Math.max(8_000, difficulty.moveTimeMs * 5),
      );
      this.#active = {
        searchId,
        resolve,
        reject,
        timeout,
        info: null,
        lines: new Map(),
      };
      this.#worker.postMessage({
        type: "SEARCH",
        searchId,
        fen,
        moveTimeMs: difficulty.moveTimeMs,
        nodeLimit: difficulty.nodeLimit,
        options: difficulty,
      });
    });
  }

  stop() {
    if (!this.#active) {
      return;
    }
    const active = this.#active;
    this.#active = null;
    clearTimeout(active.timeout);
    this.#worker?.postMessage({ type: "STOP", searchId: active.searchId });
    const error = new Error("Engine search cancelled");
    error.name = "AbortError";
    active.reject(error);
  }

  newGame() {
    this.stop();
    this.#worker?.postMessage({ type: "NEW_GAME" });
  }

  #handle(data) {
    if (data.type === "READY") {
      this.dispatchEvent(new Event("ready"));
    } else if (data.type === "ERROR") {
      if (this.#active) {
        const active = this.#active;
        this.#active = null;
        clearTimeout(active.timeout);
        active.reject(new Error(data.message));
      }
      this.dispatchEvent(new CustomEvent("error", { detail: data.message }));
    } else if (
      data.type === "BEST_MOVE" &&
      data.searchId === this.#active?.searchId
    ) {
      const active = this.#active;
      this.#active = null;
      clearTimeout(active.timeout);
      active.resolve({
        move: data.move,
        info: active.info,
        lines: [...active.lines.values()].sort(
          (left, right) => left.multipv - right.multipv,
        ),
      });
      this.dispatchEvent(
        new CustomEvent("bestmove", {
          detail: { ...data, info: active.info },
        }),
      );
    } else if (
      data.type === "INFO" &&
      data.searchId === this.#active?.searchId
    ) {
      if (
        data.info?.pv?.length &&
        (!this.#active.lines.get(data.info.multipv) ||
          data.info.depth >= this.#active.lines.get(data.info.multipv).depth)
      ) {
        this.#active.lines.set(data.info.multipv, data.info);
        if (data.info.multipv === 1) {
          this.#active.info = data.info;
        }
      }
      this.dispatchEvent(new CustomEvent("info", { detail: data }));
    }
  }
}
